/**
 * SSC progressive-disclosure mega-menu.
 *
 * Liquid owns destination rendering so navigation remains useful without
 * JavaScript. This controller only switches the server-rendered panels and
 * provides pointer and keyboard interaction.
 */
class VevorMegaMenu {
  constructor(root = document.querySelector('.vevor-mega-menu')) {
    this.root = root;
    this.categoryButtons = root
      ? Array.from(root.querySelectorAll('[data-mega-category]'))
      : [];
    this.categoryPanels = root
      ? Array.from(root.querySelectorAll('[data-category-panel]'))
      : [];
    this.groupButtons = root
      ? Array.from(root.querySelectorAll('[data-mega-group]'))
      : [];
    this.destinationPanels = root
      ? Array.from(root.querySelectorAll('[data-destination-panel]'))
      : [];

    if (!this.root || this.categoryButtons.length === 0) return;

    this.bindEvents();
    this.activateCategory(this.categoryButtons[0].dataset.megaCategory);
  }

  bindEvents() {
    this.categoryButtons.forEach((button) => {
      button.addEventListener('pointerenter', () => {
        this.activateCategory(button.dataset.megaCategory);
      });
      button.addEventListener('focus', () => {
        this.activateCategory(button.dataset.megaCategory);
      });
      button.addEventListener('click', () => {
        this.activateCategory(button.dataset.megaCategory);
        this.getActiveGroupButton()?.focus();
      });
      button.addEventListener('keydown', (event) => {
        this.handleRailKeys(event, this.categoryButtons, () => {
          this.getActiveGroupButton()?.focus();
        });
      });
    });

    this.groupButtons.forEach((button) => {
      button.addEventListener('pointerenter', () => {
        this.activateGroup(button.dataset.megaGroup);
      });
      button.addEventListener('focus', () => {
        this.activateGroup(button.dataset.megaGroup);
      });
      button.addEventListener('click', () => {
        this.activateGroup(button.dataset.megaGroup);
        this.getActiveDestinationLinks()[0]?.focus();
      });
      button.addEventListener('keydown', (event) => {
        const visibleGroups = this.getVisibleGroupButtons();
        this.handleRailKeys(
          event,
          visibleGroups,
          () => this.getActiveDestinationLinks()[0]?.focus(),
          () => this.getActiveCategoryButton()?.focus()
        );
      });
    });

    this.root.addEventListener('keydown', (event) => {
      const link = event.target.closest('.vevor-mega-destination-link');
      if (!link) return;

      const links = this.getActiveDestinationLinks();
      const index = links.indexOf(link);
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        links[(index + 1) % links.length]?.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        links[(index - 1 + links.length) % links.length]?.focus();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.getActiveGroupButton()?.focus();
      }
    });
  }

  activateCategory(category) {
    const selectedButton = this.categoryButtons.find(
      (button) => button.dataset.megaCategory === category
    );
    const selectedPanel = this.categoryPanels.find(
      (panel) => panel.dataset.categoryPanel === category
    );
    if (!selectedButton || !selectedPanel) return;

    this.categoryButtons.forEach((button) => {
      const active = button === selectedButton;
      button.classList.toggle('active', active);
      button.setAttribute('aria-expanded', String(active));
    });

    this.categoryPanels.forEach((panel) => {
      panel.hidden = panel !== selectedPanel;
    });

    const firstGroup = selectedPanel.querySelector('[data-mega-group]');
    if (firstGroup) this.activateGroup(firstGroup.dataset.megaGroup);
  }

  activateGroup(group) {
    const selectedButton = this.groupButtons.find(
      (button) => button.dataset.megaGroup === group
    );
    const selectedPanel = this.destinationPanels.find(
      (panel) => panel.dataset.destinationPanel === group
    );
    if (!selectedButton || !selectedPanel) return;

    this.groupButtons.forEach((button) => {
      const visible = !button.closest('[data-category-panel]').hidden;
      const active = visible && button === selectedButton;
      button.classList.toggle('active', active);
      button.setAttribute('aria-expanded', String(active));
    });

    this.destinationPanels.forEach((panel) => {
      panel.hidden = panel !== selectedPanel;
    });
  }

  handleRailKeys(event, buttons, moveRight, moveLeft) {
    const index = buttons.indexOf(event.currentTarget);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      buttons[(index + 1) % buttons.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      buttons[(index - 1 + buttons.length) % buttons.length]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      buttons[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      buttons[buttons.length - 1]?.focus();
    } else if (event.key === 'ArrowRight' && moveRight) {
      event.preventDefault();
      moveRight();
    } else if (event.key === 'ArrowLeft' && moveLeft) {
      event.preventDefault();
      moveLeft();
    }
  }

  getActiveCategoryButton() {
    return this.categoryButtons.find(
      (button) => button.getAttribute('aria-expanded') === 'true'
    );
  }

  getVisibleGroupButtons() {
    const panel = this.categoryPanels.find((item) => !item.hidden);
    return panel ? Array.from(panel.querySelectorAll('[data-mega-group]')) : [];
  }

  getActiveGroupButton() {
    return this.getVisibleGroupButtons().find(
      (button) => button.getAttribute('aria-expanded') === 'true'
    );
  }

  getActiveDestinationLinks() {
    const panel = this.destinationPanels.find((item) => !item.hidden);
    return panel
      ? Array.from(panel.querySelectorAll('.vevor-mega-destination-link'))
      : [];
  }

  show() {
    this.root.classList.add('active');
  }

  hide() {
    this.root.classList.remove('active');
  }

  toggle() {
    this.root.classList.toggle('active');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.vevorMegaMenu = new VevorMegaMenu();
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = VevorMegaMenu;
}
